import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('grc_token');
    const storedUser = localStorage.getItem('grc_user');
    
    if (token && storedUser) {
      // Optimistically restore the session from stored data first, so a transient
      // network/CDN failure (e.g. the first API call after an external redirect back
      // from Stripe Checkout being challenged) does NOT spuriously log the user out.
      try {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } catch (e) {
        // corrupted stored user; will be revalidated below
      }

      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
        localStorage.setItem('grc_user', JSON.stringify(response.data));
      } catch (error) {
        // Only force logout on a genuine auth failure (invalid/expired token).
        // For any other error keep the existing session intact.
        if (error.response?.status === 401) {
          logout();
        } else {
          console.warn('Auth revalidation skipped (keeping session):', error?.message);
        }
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('grc_token', access_token);
      localStorage.setItem('grc_user', JSON.stringify(userData));
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('grc_token', access_token);
      localStorage.setItem('grc_user', JSON.stringify(newUser));
      
      setUser(newUser);
      setIsAuthenticated(true);
      
      // Return success for new users
      return { 
        success: true, 
        user: newUser,
        needsVerification: false
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('grc_token');
    localStorage.removeItem('grc_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateMe(data);
      const updatedUser = response.data;
      
      setUser(updatedUser);
      localStorage.setItem('grc_user', JSON.stringify(updatedUser));
      
      return { success: true, user: updatedUser };
    } catch (error) {
      const message = error.response?.data?.detail || 'Update failed';
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('grc_user', JSON.stringify(response.data));
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return { success: false };
    }
  };

  // Email verification is disabled - all users are verified on registration
  const needsEmailVerification = () => false;

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    checkAuth,
    needsEmailVerification,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
